import React from 'react';
import ReactDOM from 'react-dom';

import { StaffAllocation } from '../../shared/types/staffAllocation';
import { Time } from '../../shared/types/time';
import {
  timeToMinutes,
  staffMinutesToTime,
  staffMinutesToRelativeTime,
} from '../../shared/lib/time';
import { HourFormat } from '../../shared/types/brand';
import { PartialStaffAllocation } from './EventTemplateForm';
import { TextButton, Slider } from '@spark-labs/avocado-ui';

const SLIDER_MINUTES_BEFORE_EVENT = 4 * 60;
const SLIDER_MINUTES_AFTER_EVENT = 4 * 60;
const SLIDER_EVENT_HEIGHT = 30;
const SLIDER_STEP_MINUTES = 5;

export interface RelatedEventTime {
  eventStart: Time;
  dayStart: Time;
  hourFormat: HourFormat;
}

export interface StaffAllocationSliderProps {
  staffAllocation: StaffAllocation | PartialStaffAllocation;
  relatedEventTime?: RelatedEventTime;
  eventDuration: number;
  onHideClick: () => void;
  onChange?: (start: number, end: number) => void;
}

export default class StaffAllocationSlider extends React.Component<StaffAllocationSliderProps> {
  private eventDuration: number;
  private sliderMinutes: number;

  constructor(props: StaffAllocationSliderProps) {
    super(props);

    this.tooltipFunction = this.tooltipFunction.bind(this);

    let eventStartMinutes;
    if (this.props.relatedEventTime) {
      let dayStartMinutes = timeToMinutes(this.props.relatedEventTime.dayStart);
      eventStartMinutes = timeToMinutes(this.props.relatedEventTime.eventStart, dayStartMinutes);
    } else {
      eventStartMinutes = 0;
    }
    let eventEndMinutes = eventStartMinutes + this.props.eventDuration;

    this.eventDuration = eventEndMinutes - eventStartMinutes;
    this.sliderMinutes =
      this.eventDuration + SLIDER_MINUTES_BEFORE_EVENT + SLIDER_MINUTES_AFTER_EVENT;
  }

  public componentDidMount() {
    // Add the event length inside the slider
    let thisElement = ReactDOM.findDOMNode(this) as Element;
    let sliderWrapper = thisElement.querySelector('.Slider');

    if (!sliderWrapper) return;

    let bb = sliderWrapper.getBoundingClientRect();
    let sliderWidth = bb.width;

    let pixelsPerMinute = sliderWidth / this.sliderMinutes;

    let sliderCenter = Math.floor(sliderWidth / 2);

    let eventWidth = this.eventDuration * pixelsPerMinute;
    let eventLeft = sliderCenter - Math.floor(eventWidth / 2);
    let eventTop = (bb.height - SLIDER_EVENT_HEIGHT) / 2;

    let eventSpan = document.createElement('div');
    eventSpan.className = 'absolute bg-orange-light';
    eventSpan.style.top = `${eventTop}px`;
    eventSpan.style.left = `${eventLeft}px`;
    eventSpan.style.width = `${eventWidth}px`;
    eventSpan.style.height = `${SLIDER_EVENT_HEIGHT}px`;
    eventSpan.style.zIndex = '0';

    sliderWrapper.appendChild(eventSpan);
  }

  public render() {
    let startPosition = this.props.staffAllocation.startTime;
    let endPosition = this.props.staffAllocation.startTime + this.props.staffAllocation.duration;

    return (
      <div className="relative w-full">
        <div className="z-20 absolute" style={{ top: 3, right: 3 }}>
          <TextButton
            onClick={this.props.onHideClick}
            className="p-1 text-xxs border-none bg-grey-darker underline text-white"
          >
            Hide
          </TextButton>
        </div>
        <Slider
          rangeMin={-SLIDER_MINUTES_BEFORE_EVENT}
          rangeMax={this.eventDuration + SLIDER_MINUTES_AFTER_EVENT}
          startPosition={startPosition}
          endPosition={endPosition}
          step={SLIDER_STEP_MINUTES}
          tooltipFunction={this.tooltipFunction}
          onChange={this.props.onChange}
        />
      </div>
    );
  }

  private tooltipFunction(minutes: number): string {
    let timeString;

    // Sometimes we get a weird decimal from the slider, make sure it's rounded to the closes
    // integer
    minutes = Math.round(minutes);

    if (this.props.relatedEventTime) {
      timeString = staffMinutesToTime(
        minutes,
        this.props.relatedEventTime.eventStart,
        this.props.relatedEventTime.hourFormat
      );
    } else {
      timeString = staffMinutesToRelativeTime(minutes);
    }
    return timeString;
  }
}
